/**
 * Shared icon component.
 *
 * Maps the legacy SVG-sprite names (formerly `images/icons.svg#icon-NAME`,
 * rendered via per-file `<use xlinkHref>` wrappers) onto lucide-react
 * components. Every historical call site keeps working unchanged:
 *
 *   <Icon name="logout" />            → lucide <LogOut />
 *   <Icon name="clock" color="#f54b01" size={18} />
 *
 * lucide icons are stroke-based and default to `currentColor`, so they inherit
 * text colour from their container (e.g. the active nav pill) automatically.
 * Where an icon lives inside `.icon-wrap`, the existing CSS forces the svg to
 * fill its box, so the wrapper still controls the rendered size.
 */
import React from "react";
import {
  Settings,
  LogOut,
  SquarePen,
  X,
  Palmtree,
  Clock,
  Eye,
  EyeOff,
  User,
  Check,
  Mail,
  Table,
  ArrowLeftRight,
  CalendarClock,
  Save,
  Plus,
  Home,
  Folder,
  Network,
  Calendar,
  Bell,
  Plane,
  AlarmClock,
  CloudSun,
} from "lucide-react";
import PropTypes from "prop-types";

// Legacy sprite name → lucide component.
const ICON_MAP = {
  settings: Settings,
  logout: LogOut,
  create: SquarePen,
  close: X,
  leaves: Palmtree,
  clock: Clock,
  eye: Eye,
  eyeslash: EyeOff,
  user: User,
  ok: Check,
  mail: Mail,
  table: Table,
  swap: ArrowLeftRight,
  shift: CalendarClock,
  save: Save,
  plus: Plus,
  home: Home,
  folder: Folder,
  chart: Network,
  calendar: Calendar,
  // permission-group icons (RoleModal)
  bell: Bell,
  plane: Plane,
  "alarm-clock": AlarmClock,
  "partly-cloudy": CloudSun,
};

const Icon = ({ name, color, size, ...rest }) => {
  const Cmp = ICON_MAP[name] || Settings;
  return (
    <Cmp
      className={`icon icon-${name}`}
      color={color}
      {...(size ? { size } : {})}
      {...rest}
    />
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Icon;
